import { getRegistryItem } from '../../components/editor/registry';

export const ResumeSchemaService = {
  /**
   * Initializes default sectionSettings and sections list if missing.
   * Self-healing backward compatibility layer.
   */
  ensureSchema(resume) {
    if (!resume) return resume;
    const next = JSON.parse(JSON.stringify(resume));
    
    if (!next.schemaVersion) {
      next.schemaVersion = 2;
    }
    
    if (!next.sections) {
      next.sections = ['contact', 'summary', 'experience', 'education', 'skills', 'projects'];
    }
    
    if (!next.sectionSettings) {
      next.sectionSettings = {};
    }
    
    // Ensure all default sections are marked as enabled/visible
    next.sections.forEach((id, idx) => {
      if (!next.sectionSettings[id]) {
        next.sectionSettings[id] = {
          enabled: true,
          visible: true,
          collapsed: false,
          order: idx
        };
      }
    });

    return next;
  },

  enableSection(resume, sectionId) {
    const next = this.ensureSchema(resume);
    if (!next.sections.includes(sectionId)) {
      next.sections.push(sectionId);
    }
    next.sectionSettings[sectionId] = {
      ...(next.sectionSettings[sectionId] || {}),
      enabled: true,
      visible: true,
      collapsed: false,
      order: next.sections.length - 1
    };
    return next;
  },

  disableSection(resume, sectionId) {
    const next = this.ensureSchema(resume);
    next.sections = next.sections.filter(id => id !== sectionId);
    if (next.sectionSettings[sectionId]) {
      next.sectionSettings[sectionId].enabled = false;
      next.sectionSettings[sectionId].visible = false;
    }
    return next;
  },

  hideSection(resume, sectionId) {
    const next = this.ensureSchema(resume);
    if (next.sectionSettings[sectionId]) {
      next.sectionSettings[sectionId].visible = false;
    }
    return next;
  },

  showSection(resume, sectionId) {
    const next = this.ensureSchema(resume);
    if (next.sectionSettings[sectionId]) {
      next.sectionSettings[sectionId].visible = true;
    }
    return next;
  },

  toggleCollapsed(resume, sectionId) {
    const next = this.ensureSchema(resume);
    if (next.sectionSettings[sectionId]) {
      next.sectionSettings[sectionId].collapsed = !next.sectionSettings[sectionId].collapsed;
    } else {
      next.sectionSettings[sectionId] = { enabled: true, visible: true, collapsed: true };
    }
    return next;
  },

  moveSection(resume, sectionId, direction) {
    const next = this.ensureSchema(resume);
    const index = next.sections.indexOf(sectionId);
    if (index === -1) return next;

    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= next.sections.length) return next;

    // Swap sections
    const temp = next.sections[index];
    next.sections[index] = next.sections[targetIndex];
    next.sections[targetIndex] = temp;

    // Re-sync order numbers
    next.sections.forEach((id, idx) => {
      if (next.sectionSettings[id]) {
        next.sectionSettings[id].order = idx;
      }
    });

    return next;
  },

  getOrderedSections(resume) {
    const safe = this.ensureSchema(resume);
    return safe.sections
      .filter(id => safe.sectionSettings[id]?.enabled)
      .map(id => ({
        id,
        ...getRegistryItem(id),
        settings: safe.sectionSettings[id]
      }))
      .filter(s => s.id);
  },

  getVisibleSections(resume) {
    const safe = this.ensureSchema(resume);
    return safe.sections
      .filter(id => {
        const settings = safe.sectionSettings[id];
        return settings?.enabled && settings?.visible;
      });
  }
};
